import React, { useState, useEffect } from 'react'
import { useDispatch } from 'react-redux'
import isEmpty from 'lodash/isEmpty'
import Typography from 'britive-design-system/core/components/typography'
import Textfield from 'britive-design-system/core/components/textfield'
import Button from 'britive-design-system/core/components/button'
import Select from 'britive-design-system/core/components/select'
import Tooltip from 'britive-design-system/core/components/tooltip'
import Spinner from 'britive-design-system/core/components/spinner'
import { connectionTypeName, ticketTypes } from './constants'
import TicketList from './TicketList'
import editPencil from 'static/images/edit-icon.svg'
import clearIcon from 'static/images/close-dark.svg'
import search from 'static/images/search.svg'
import { removeTicketSearchResultsForAccessProfile } from 'action_creators/access'
import { removeTicketSearchResultsForResourceProfile } from 'action_creators/serverAccess/myResources'

const CSS_CLASS_PREFIX = 'common-approval-modal-itsm-checkout-settings'

const ITSMCheckoutSettings = ({
  itsmData = {},
  checkoutItsmData = {},
  onChange,
  onTicketSearch,
  ticketSearchResults = [],
  findTicketDetailsById,
  setErrors,
}) => {
  const [selectedTicketDetails, setSelectedTicketDetails] = useState({})
  const [showTicketDetails, setShowTicketDetails] = useState(false)
  const [loading, setLoading] = useState(false)
  const dispatch = useDispatch()

  useEffect(() => {
    if (itsmData?.supportedTicketTypes?.length === 1) {
      onChange({
        ...checkoutItsmData,
        ticketType: itsmData.supportedTicketTypes[0],
      })
    }
  }, [itsmData])

  if (isEmpty(itsmData?.supportedTicketTypes)) {
    return null
  }

  const handleTicketSearch = () => {
    if (isEmpty(checkoutItsmData.ticketType)) {
      setErrors(['Ticket Type is required for searching tickets.'])
      return
    }

    onTicketSearch(checkoutItsmData.ticketType, checkoutItsmData?.ticketId)
  }

  const handleClearAll = () => {
    dispatch(removeTicketSearchResultsForAccessProfile())
    dispatch(removeTicketSearchResultsForResourceProfile())
    setSelectedTicketDetails({})
    setShowTicketDetails(false)
    onChange({
      ...checkoutItsmData,
      ticketId: '',
    })
  }

  const handleKeyUp = e => {
    if (e.keyCode === 13) {
      if (isEmpty(checkoutItsmData.ticketType)) {
        setErrors(['Ticket Type is required for searching tickets.'])
        return
      }

      onTicketSearch(checkoutItsmData.ticketType, e.target.value)
    }
  }

  const getExactTicketDetails = async () => {
    if (isEmpty(checkoutItsmData.ticketType) || isEmpty(checkoutItsmData.ticketId)) {
      setErrors(['Ticket Id is required for fetching the details..'])
      return
    }

    try {
      setLoading(true)
      const { value } = await findTicketDetailsById(
        checkoutItsmData.ticketType,
        checkoutItsmData.ticketId
      )
      if (!isEmpty(value?.data?.ticketDetails)) {
        setSelectedTicketDetails(value.data.ticketDetails)
        setShowTicketDetails(true)
      } else {
        setErrors([
          value?.data?.message ||
            `The Ticket Id "${checkoutItsmData.ticketId}" is either invalid, or doesn't match the filter criteria.`,
        ])
      }
    } catch {
      setErrors(['Unable to fetch the ticket details.'])
    } finally {
      setLoading(false)
    }
  }

  return (
    <>
      {loading && <Spinner size="medium" message="" overlay />}
      <div className={`${CSS_CLASS_PREFIX}-connection-type`}>
        <Typography variant="label2">Type</Typography>
        <Typography variant="label1">
          {connectionTypeName[itsmData.connectionType]}
        </Typography>
      </div>
      {itsmData?.supportedTicketTypes?.length === 1 ? (
        <div className={`${CSS_CLASS_PREFIX}-ticket-type-single`}>
          <Typography variant="label2">Ticket Type</Typography>
          <Typography variant="label1">
            {ticketTypes[itsmData.supportedTicketTypes[0]]}
          </Typography>
        </div>
      ) : (
        <div className={`${CSS_CLASS_PREFIX}-ticket-type-multiple`}>
          <Select
            width="450px"
            label="Ticket Type"
            value={
              isEmpty(checkoutItsmData.ticketType)
                ? {}
                : { title: checkoutItsmData.ticketType }
            }
            options={itsmData.supportedTicketTypes.map(ticketType => ({
              title: ticketType,
            }))}
            getOptionLabel={option => ticketTypes[option?.title]}
            onChange={(_, value) => {
              onChange({
                ticketType: value.title,
                ticketId: '',
              })
              setSelectedTicketDetails({})
              setShowTicketDetails(false)
              dispatch(removeTicketSearchResultsForAccessProfile())
              dispatch(removeTicketSearchResultsForResourceProfile())
            }}
          />
        </div>
      )}
      <div className={`${CSS_CLASS_PREFIX}-ticket-id`}>
        <Typography variant="label2">Ticket ID</Typography>
        {isEmpty(selectedTicketDetails) ? (
          <>
            <Textfield
              width="450px"
              placeholder="Search ticket or enter ticket ID"
              onChange={({ target }) =>
                onChange({
                  ...checkoutItsmData,
                  ticketId: target?.value,
                })
              }
              onKeyUp={handleKeyUp}
              value={checkoutItsmData?.ticketId || ''}
              iconPosition="right"
              icon={
                <div className={`${CSS_CLASS_PREFIX}-ticket-search-icons`}>
                  {!isEmpty(ticketSearchResults?.tickets) ||
                  checkoutItsmData?.ticketId ? (
                    <Tooltip title="Clear all search." position="top">
                      <img
                        src={clearIcon}
                        className={`${CSS_CLASS_PREFIX}-ticket-search-icons-clear`}
                        onClick={handleClearAll}
                      />
                    </Tooltip>
                  ) : null}
                  <Tooltip title="Search" position="top">
                    <img src={search} onClick={handleTicketSearch} />
                  </Tooltip>
                </div>
              }
            />
            {isEmpty(ticketSearchResults?.tickets) && (
              <div className={`${CSS_CLASS_PREFIX}-view-details`}>
                <Button
                  variant="secondary"
                  size="small"
                  onClick={getExactTicketDetails}
                >
                  View Details
                </Button>
              </div>
            )}
          </>
        ) : (
          <div className={`${CSS_CLASS_PREFIX}-ticket-link`}>
            <Typography variant="label1">
              <a href={selectedTicketDetails.link} target="_blank" rel="noreferrer">
                {selectedTicketDetails.number}
              </a>
            </Typography>
            <Tooltip title="Edit" position="right">
              <img
                src={editPencil}
                onClick={() => {
                  setSelectedTicketDetails({})
                  setShowTicketDetails(false)
                  onChange({
                    ...checkoutItsmData,
                    ticketId: '',
                  })
                }}
              />
            </Tooltip>
          </div>
        )}
      </div>

      {isEmpty(selectedTicketDetails) && (
        <TicketList
          list={ticketSearchResults}
          onClick={ticketDetails => {
            setSelectedTicketDetails(ticketDetails)
            onChange({
              ...checkoutItsmData,
              ticketId: ticketDetails.number,
            })
            dispatch(removeTicketSearchResultsForAccessProfile())
            dispatch(removeTicketSearchResultsForResourceProfile())
          }}
        />
      )}

      {!showTicketDetails && !isEmpty(selectedTicketDetails) && (
        <div className={`${CSS_CLASS_PREFIX}-view-details`}>
          <Button
            variant="secondary"
            size="small"
            onClick={() => setShowTicketDetails(!showTicketDetails)}
          >
            View Details
          </Button>
        </div>
      )}

      {showTicketDetails && !isEmpty(selectedTicketDetails) && (
        <>
          <div className={`${CSS_CLASS_PREFIX}-summary`}>
            <Typography variant="label2">Summary</Typography>
            <Typography variant="label1">{selectedTicketDetails.title}</Typography>
          </div>
          <div className={`${CSS_CLASS_PREFIX}-assignee`}>
            <Typography variant="label2">Assignee</Typography>
            <Typography variant="label1">
              {selectedTicketDetails.assigneeName}
            </Typography>
          </div>
          <div className={`${CSS_CLASS_PREFIX}-severity`}>
            <Typography variant="label2">Severity</Typography>
            <Typography variant="label1">
              {selectedTicketDetails.severity || 'N/A'}
            </Typography>
          </div>
          <div className={`${CSS_CLASS_PREFIX}-status`}>
            <Typography variant="label2">Status</Typography>
            <Typography variant="label1">{selectedTicketDetails.status}</Typography>
          </div>
        </>
      )}
    </>
  )
}

export default ITSMCheckoutSettings
